import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import FlagIcons from './FlagIcons';
import { Eye } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import socket from '../utils/socket';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * `PW` (Pending Withdraw) header badge — sits to the left of the `N` badge.
 * Lists Withdraw Group entries still in status='pending' older than the
 * chosen threshold (default 30 min). Click a row → jump straight to the
 * originating group message (even if it's from a previous day).
 *
 * Design mirrors PT — same size, same dropdown UX — with a red accent so
 * "action needed" reads faster than the amber PT tag.
 */
const PW_THRESHOLD_KEY = 'pw_threshold_minutes';
const PW_PRESETS = [
  { label: '0m',  value: 0 },
  { label: '15m', value: 15 },
  { label: '30m', value: 30 },
  { label: '45m', value: 45 },
];

const InstantAccessPW = ({ variant = 'dark' }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ count: 0, items: [] });
  const [threshold, setThreshold] = useState(() => {
    const stored = parseInt(localStorage.getItem(PW_THRESHOLD_KEY) ?? '30', 10);
    return Number.isFinite(stored) && stored >= 0 ? stored : 30;
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customVal, setCustomVal] = useState('');
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const persistThreshold = (mins) => {
    const n = parseInt(mins, 10);
    if (!Number.isFinite(n) || n < 0) return;
    setThreshold(n);
    localStorage.setItem(PW_THRESHOLD_KEY, String(n));
    setPickerOpen(false);
  };

  const fmtThresholdLabel = (m) => (m >= 60 ? `${m % 60 === 0 ? (m/60) : (m/60).toFixed(1)}h` : `${m}m`);

  useEffect(() => {
    let cancelled = false;
    // Fix doc #4 — instant-cache: paint last-known pending list, then refresh.
    const _cached = readInstantCache('pending_withdraw_list', user?.id);
    if (_cached && !cancelled) setData(_cached);
    const load = async () => {
      try {
        const res = await axios.get(`${API}/withdraw-entries/pending-list`, {
          params: { threshold_minutes: threshold, user_id: user?.id },
        });
        if (!cancelled) {
          const d = res.data || { count: 0, items: [] };
          setData(d);
          writeInstantCache('pending_withdraw_list', user?.id, d);
        }
      } catch (_) { /* transient */ }
    };
    load();
    // Poll every 20s as a safety net — real-time refresh is driven by the
    // `pw_updated` socket event below so users see PW changes in ~1s instead
    // of waiting for the next poll cycle.
    const t = setInterval(() => { if (document.visibilityState === 'hidden') return; load(); }, 20000);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);

    // Real-time PW refresh via Socket.IO. Server emits `pw_updated` on:
    //   - Coin Cut broadcast (new pending entry)
    //   - Mark-paid / revert (single + bulk)
    //   - Cancel
    // We do a lightweight debounce so several rapid emits collapse into
    // one fetch.
    let debounceHandle = null;
    const onPwUpdated = () => {
      if (debounceHandle) clearTimeout(debounceHandle);
      debounceHandle = setTimeout(() => { load(); }, 250);
    };
    socket.on('pw_updated', onPwUpdated);

    return () => {
      cancelled = true;
      clearInterval(t);
      if (debounceHandle) clearTimeout(debounceHandle);
      socket.off('pw_updated', onPwUpdated);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [threshold, user?.id]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  // P3/P4 — only one of N/G/PT/PW open at a time.
  useEffect(() => {
    const onOther = (e) => { if (e.detail !== 'PW') setOpen(false); };
    window.addEventListener('dws:ia-open', onOther);
    return () => window.removeEventListener('dws:ia-open', onOther);
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem('pw_reopen') === '1') {
        sessionStorage.removeItem('pw_reopen');
        setOpen(true);
      }
    } catch (_) {}
  }, []);

  // Redirect to the group + auto-scroll to the pending withdraw entry. The
  // group chat page listens for a `scroll_to_message` search param (set by
  // GroupChatPage) so the pending row shows up even if it's from a prior day.
  const gotoEntry = (it) => {
    if (!it?.group_id) return;
    try { sessionStorage.setItem('pw_reopen', '1'); } catch (_) {}
    const url = it.group_message_id
      ? `/groups/${it.group_id}?scroll_to=${encodeURIComponent(it.group_message_id)}`
      : `/groups/${it.group_id}`;
    // Session-32 fix: pass the CURRENT path + query as `from` so the group's
    // back button returns to exactly where the user opened PW (same tab,
    // same MB filter, same URL). GroupChatPage validates the path is a real
    // route before honoring it, so a bad state can't cause a white screen.
    const fromPath = (location.pathname || '') + (location.search || '');
    navigate(url, { state: { from: fromPath && fromPath.startsWith('/') ? fromPath : undefined } });
    setOpen(false);
  };

  const count = data.count || 0;

  const fmtWaited = (m) => {
    if (!m || m < 0) return '0m';
    if (m >= 60 * 24) return `${Math.floor(m / (60 * 24))}d`;
    if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
    return `${m}m`;
  };

  const fmtAmt = (a) => {
    const n = Number(a) || 0;
    return n.toLocaleString('en-IN');
  };

  return (
    <div className="relative" ref={ref} data-testid="pending-withdraw-pw">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => { const nv = !v; if (nv) window.dispatchEvent(new CustomEvent('dws:ia-open', { detail: 'PW' })); return nv; }); }}
        className={variant === 'light'
          ? "relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-500 hover:bg-red-600 text-white font-black text-[13px] shadow-md transition-colors"
          : "relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-400/25 hover:bg-red-400/40 text-white font-black text-[13px] border border-red-200/60 shadow-inner transition-colors"}
        data-testid="pw-btn"
        title={`Pending Withdraws — group entries not paid for ${fmtThresholdLabel(threshold)}+`}
      >
        PW
        {count > 0 && (
          <span
            className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow"
            data-testid="pw-count"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        <div
          className="fixed left-3 right-3 top-14 z-[80] sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:w-[340px] bg-white text-slate-800 rounded-xl shadow-2xl border border-slate-200 max-h-[70vh] overflow-hidden flex flex-col"
          data-testid="pw-menu"
        >
          <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 border-b border-slate-100 flex items-center justify-between">
            <span>Pending — awaiting payment</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPickerOpen(v => !v); }}
              className="text-red-700 hover:text-red-900 font-bold flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-red-50 border border-transparent hover:border-red-200 transition"
              title="Change threshold"
              data-testid="pw-threshold-btn"
            >
              {count} · {fmtThresholdLabel(threshold)}+ <span className="text-[9px]">▾</span>
            </button>
          </div>
          {pickerOpen && (
            <div className="px-3 py-2 border-b border-slate-100 bg-red-50/40" data-testid="pw-threshold-picker">
              <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Show withdraws pending more than</div>
              <div className="flex gap-1.5 mb-2 flex-wrap">
                {PW_PRESETS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => persistThreshold(p.value)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${
                      threshold === p.value
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-red-50'
                    }`}
                    data-testid={`pw-preset-${p.value}`}
                  >{p.label}</button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min="0"
                  max="1440"
                  placeholder="Custom min"
                  value={customVal}
                  onChange={(e) => setCustomVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') persistThreshold(customVal); }}
                  className="flex-1 px-2 py-1 border border-slate-200 rounded text-[11px] focus:outline-none focus:border-red-500"
                  data-testid="pw-custom-input"
                />
                <button
                  onClick={() => persistThreshold(customVal)}
                  className="px-2.5 py-1 bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold rounded"
                  data-testid="pw-custom-apply"
                >Set</button>
              </div>
            </div>
          )}
          <div className="overflow-y-auto">
            {(data.items || []).length === 0 ? (
              <div className="px-3 py-6 text-xs text-center text-slate-400">All withdraws paid ✓</div>
            ) : (
              (data.items || []).map((it) => (
                <div
                  key={it.entry_id}
                  className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-red-50"
                  data-testid={`pw-item-${it.entry_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-[13px] truncate">{it.customer_name}</span>
                      {/* MB badge + inline 👁 View — session-32 fix: eye icon
                          moved here so it's always visible even if the outer
                          menu clips on one side. Tap = jump to the pending
                          withdraw group message directly. */}
                      {it.customer_mb && (
                        <span
                          className="text-[10px] font-bold px-1.5 py-[1px] rounded bg-indigo-100 text-indigo-700 uppercase leading-none"
                          data-testid={`pw-mb-${it.entry_id}`}
                        >{it.customer_mb}</span>
                      )}
                      <button
                        onClick={() => gotoEntry(it)}
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex-shrink-0"
                        title="View this pending withdraw in the group"
                        data-testid={`pw-view-${it.entry_id}`}
                      >
                        <Eye size={12} weight="bold" />
                      </button>
                      {it.customer_flag && <FlagIcons flag={it.customer_flag} size={10} />}
                    </div>
                    <div className="text-[12px] font-bold text-red-700 mt-0.5">₹ {fmtAmt(it.amount)}</div>
                  </div>
                  <div className="text-[10px] font-bold text-red-700 whitespace-nowrap flex-shrink-0">
                    {fmtWaited(it.waited_minutes || 0)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InstantAccessPW;
